import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { referralAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, GraduationCap, CheckCircle, Gift, Users } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralInfo, setReferralInfo] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const code = searchParams.get('ref');
    if (code) {
      setReferralCode(code);
      checkReferralCode(code);
    }
  }, [searchParams]);

  const checkReferralCode = async (code) => {
    if (!code) {
      setReferralInfo(null);
      return;
    }
    try {
      const response = await referralAPI.check(code);
      setReferralInfo(response.data);
    } catch {
      setReferralInfo(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await register({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: 'student'
      }, referralCode);
      
      // Check if email verification is required
      if (result?.user?.email_verified === false) {
        toast.success('Registration successful! Please check your email to verify your account before logging in.');
        navigate('/login?message=verify');
      } else {
        toast.success('Registration successful! Let\'s build your profile.');
        navigate('/profile-builder');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Access to 100+ partner universities",
    "Personalized course recommendations",
    "Application tracking dashboard",
    "Expert guidance from IAG qualified advisors"
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 flex transition-colors">
      {/* Left Side - Benefits */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-teal-100 dark:from-slate-800 via-[#e9ecef] dark:via-slate-800 to-[#f8f9fa] dark:to-slate-900 p-12 items-center">
        <div className="max-w-lg">
          <Link to="/">
            <img src={LOGO_URL} alt="H E Direct" className="h-16 mb-8" />
          </Link>
          <h2 className="text-3xl font-semibold text-gray-800 dark:text-white mb-6">
            Start Your Journey to Study Abroad
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
            Join thousands of students who have achieved their dreams with H E Direct&apos;s expert guidance.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                <CheckCircle className="w-5 h-5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                {benefit}
              </li>
            ))}
          </ul>
          
          <div className="mt-12 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-semibold text-teal-600 dark:text-teal-400">740+</div>
              <div className="text-gray-600 dark:text-gray-300">Students studying at their dream universities</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center lg:hidden">
            <Link to="/">
              <img src={LOGO_URL} alt="H E Direct" className="h-12 mx-auto mb-4" />
            </Link>
          </div>
          
          <div>
            <h1 className="text-3xl font-semibold text-gray-800 dark:text-white mb-2">Create Account</h1>
            <p className="text-gray-500 dark:text-gray-400">Register as a student to get started</p>
          </div>

          {/* Referral Banner */}
          {referralInfo && (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-green-600 font-medium">Referred by {referralInfo.name}</p>
                  <p className="text-gray-500 text-sm">{referralInfo.company || 'H E Direct'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Invalid Referral Warning */}
          {referralCode && !referralInfo && (
            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-orange-600 font-medium">Invalid or Inactive Referral Code</p>
                  <p className="text-gray-500 text-sm">The referral code &quot;{referralCode}&quot; is not valid. The agent may not be approved yet. You can still register without a referral.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-gray-600 dark:text-gray-300">Full Name</Label>
              <Input
                id="full_name"
                type="text"
                placeholder="John Doe"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-12 dark:text-white"
                required
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-600 dark:text-gray-300">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-12 dark:text-white"
                required
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-600 dark:text-gray-300">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+92 300 1234567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-12 dark:text-white"
                data-testid="register-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-600 dark:text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-12 pr-10 dark:text-white"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-600 dark:text-gray-300">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-12 dark:text-white"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold h-12 rounded-none"
              data-testid="register-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="text-center space-y-4">
            <p className="text-gray-500 dark:text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-600 hover:underline font-medium">
                Sign in
              </Link>
            </p>
            <p className="text-gray-500 dark:text-gray-400">
              Are you an agent?{' '}
              <Link to="/agent-register" className="text-teal-600 hover:underline font-medium">
                Register as Agent
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
